import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { entrepriseService } from '../services/entreprise.service';
import './Etiquettes.css';

interface Produit {
  id: number;
  nom: string;
  codeBarres: string;
  reference: string;
  categorie: string;
  marque: string;
  prixOrdinaire: number;
  prixRevendeur: number;
  prixTechnicien: number;
}

const fmt = (n: number) => Math.round(Number(n)).toLocaleString('fr-FR');

const formatCodeBarres = (code: string) => {
  // Affichage visuel simple du code-barres en barres CSS
  return code;
};

export default function Etiquettes() {
  const [produits, setProduits] = useState<Produit[]>([]);
  const [selectionnes, setSelectionnes] = useState<number[]>([]);
  const [chargement, setChargement] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [config, setConfig] = useState({
    taille: 'standard' as 'petite' | 'standard' | 'grande',
    afficherPrixRevendeur: false,
    afficherPrixTechnicien: false,
    afficherCodeBarres: true,
    afficherReference: true,
    afficherMarque: true,
    afficherCategorie: false,
    nomEntreprise: 'NexaCom',
    copies: 1,
    typePrix: 'ordinaire' as 'ordinaire' | 'revendeur' | 'technicien',
  });

  useEffect(() => {
    chargerProduits();
    // Pré-remplit le nom d'entreprise des étiquettes avec le vrai nom (modifiable ensuite).
    entrepriseService.getInfos().then((infos) => {
      if (infos.nom) setConfig(c => ({ ...c, nomEntreprise: infos.nom }));
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => chargerProduits(), 300);
    return () => clearTimeout(timer);
  }, [recherche]);

  const chargerProduits = async () => {
    try {
      const res = await api.get('/produits', {
        params: recherche ? { recherche } : {}
      });
      setProduits(res.data);
    } catch (e) {
    } finally {
      setChargement(false);
    }
  };

  const toggleSelection = (id: number) => {
    setSelectionnes(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectionnerTous = () => {
    if (selectionnes.length === produits.length) {
      setSelectionnes([]);
    } else {
      setSelectionnes(produits.map(p => p.id));
    }
  };

  const produitsSelectionnes = produits.filter(p => selectionnes.includes(p.id));

  const getPrix = (p: Produit) => {
    if (config.typePrix === 'revendeur') return p.prixRevendeur;
    if (config.typePrix === 'technicien') return p.prixTechnicien;
    return p.prixOrdinaire;
  };

  const imprimer = () => window.print();

  const tailleConfig = {
    petite: { width: '6cm', height: '4cm', fontSize: '10px' },
    standard: { width: '8cm', height: '5cm', fontSize: '12px' },
    grande: { width: '10cm', height: '6cm', fontSize: '14px' },
  };

  if (chargement) return <div className="page-chargement">Chargement...</div>;

  return (
    <div className="etiquettes-page">
      {/* PANNEAU DE CONTRÔLE */}
      <div className="etiquettes-controle no-print">
        <div className="controle-header">
          <div>
            <h1>Impression d'étiquettes</h1>
            <p>Sélectionnez les produits et configurez vos étiquettes</p>
          </div>
          <button
            className="btn-imprimer"
            onClick={imprimer}
            disabled={selectionnes.length === 0}>
            🖨️ Imprimer {selectionnes.length > 0 ? `(${selectionnes.length * config.copies} étiquettes)` : ''}
          </button>
        </div>

        <div className="controle-body">
          {/* SÉLECTION PRODUITS */}
          <div className="controle-section">
            <div className="section-titre">Sélection des produits</div>
            <div className="search-bar" style={{ marginBottom: 10 }}>
              <input
                value={recherche}
                onChange={e => setRecherche(e.target.value)}
                placeholder="🔍 Rechercher un produit..."
              />
            </div>
            <div className="select-all-bar">
              <button onClick={selectionnerTous}>
                {selectionnes.length === produits.length ? '☑ Tout désélectionner' : '☐ Tout sélectionner'}
              </button>
              <span>{selectionnes.length} produit{selectionnes.length > 1 ? 's' : ''} sélectionné{selectionnes.length > 1 ? 's' : ''}</span>
            </div>
            <div className="produits-liste">
              {produits.map(p => (
                <div key={p.id}
                  className={`produit-item ${selectionnes.includes(p.id) ? 'selectionne' : ''}`}
                  onClick={() => toggleSelection(p.id)}>
                  <input
                    type="checkbox"
                    checked={selectionnes.includes(p.id)}
                    onChange={() => toggleSelection(p.id)}
                    onClick={e => e.stopPropagation()}
                  />
                  <div className="pi-info">
                    <div className="pi-nom">{p.nom}</div>
                    <div className="pi-prix">{fmt(p.prixOrdinaire)} FCFA</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CONFIGURATION */}
          <div className="controle-section">
            <div className="section-titre">Configuration</div>

            <div className="config-group">
              <label>Taille de l'étiquette</label>
              <div className="taille-options">
                {(['petite', 'standard', 'grande'] as const).map(t => (
                  <button key={t}
                    className={config.taille === t ? 'active' : ''}
                    onClick={() => setConfig(c => ({ ...c, taille: t }))}>
                    {t === 'petite' ? '6×4 cm' : t === 'standard' ? '8×5 cm' : '10×6 cm'}
                    <span>{t === 'petite' ? 'Petite' : t === 'standard' ? 'Standard' : 'Grande'}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="config-group">
              <label>Type de prix à afficher</label>
              <div className="taille-options">
                {(['ordinaire', 'revendeur', 'technicien'] as const).map(t => (
                  <button key={t}
                    className={config.typePrix === t ? 'active' : ''}
                    onClick={() => setConfig(c => ({ ...c, typePrix: t }))}>
                    {t === 'ordinaire' ? 'Public' : t === 'revendeur' ? 'Revendeur' : 'Technicien'}
                  </button>
                ))}
              </div>
            </div>

            <div className="config-group">
              <label>Nombre de copies par produit</label>
              <div className="copies-input">
                <button onClick={() => setConfig(c => ({ ...c, copies: Math.max(1, c.copies - 1) }))}>−</button>
                <span>{config.copies}</span>
                <button onClick={() => setConfig(c => ({ ...c, copies: Math.min(20, c.copies + 1) }))}>+</button>
              </div>
            </div>

            <div className="config-group">
              <label>Éléments à afficher</label>
              <div className="options-liste">
                {[
                  { key: 'afficherCodeBarres', label: '📊 Code-barres' },
                  { key: 'afficherReference', label: '🔖 Référence interne' },
                  { key: 'afficherMarque', label: '🏷️ Marque' },
                  { key: 'afficherCategorie', label: '📁 Catégorie' },
                ].map(opt => (
                  <label key={opt.key} className="opt-toggle">
                    <input
                      type="checkbox"
                      checked={(config as any)[opt.key]}
                      onChange={e => setConfig(c => ({ ...c, [opt.key]: e.target.checked }))}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="config-group">
              <label>Nom entreprise sur l'étiquette</label>
              <input
                value={config.nomEntreprise}
                onChange={e => setConfig(c => ({ ...c, nomEntreprise: e.target.value }))}
                placeholder="Ex: NexaCom"
              />
            </div>
          </div>

          {/* APERÇU */}
          <div className="controle-section">
            <div className="section-titre">Aperçu</div>
            {selectionnes.length === 0 ? (
              <div className="apercu-vide">
                Sélectionnez des produits pour voir l'aperçu
              </div>
            ) : (
              <div className="apercu-etiquette"
                style={{
                  width: tailleConfig[config.taille].width,
                  minHeight: tailleConfig[config.taille].height,
                  fontSize: tailleConfig[config.taille].fontSize,
                }}>
                {(() => {
                  const p = produitsSelectionnes[0];
                  return (
                    <>
                      <div className="etq-header">
                        <span className="etq-entreprise">{config.nomEntreprise}</span>
                        {config.afficherCategorie && p.categorie && (
                          <span className="etq-categorie">{p.categorie}</span>
                        )}
                      </div>
                      <div className="etq-nom">{p.nom}</div>
                      {config.afficherMarque && p.marque && (
                        <div className="etq-marque">{p.marque}</div>
                      )}
                      {config.afficherReference && p.reference && (
                        <div className="etq-ref">Réf: {p.reference}</div>
                      )}
                      <div className="etq-prix">
                        {fmt(getPrix(p))} FCFA
                        {config.typePrix !== 'ordinaire' && (
                          <span className="etq-prix-type">
                            ({config.typePrix === 'revendeur' ? 'Revendeur' : 'Technicien'})
                          </span>
                        )}
                      </div>
                      {config.afficherCodeBarres && p.codeBarres && (
                        <div className="etq-code">
                          <div className="etq-barres">
                            {p.codeBarres.split('').map((c, i) => (
                              <div key={i} className="barre" style={{
                                width: parseInt(c) % 2 === 0 ? '1px' : '2px',
                                background: '#000',
                                height: '100%',
                              }} />
                            ))}
                          </div>
                          <div className="etq-code-txt">{p.codeBarres}</div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ZONE D'IMPRESSION */}
      <div className="etiquettes-impression">
        {produitsSelectionnes.length === 0 ? (
          <div className="impression-vide no-print">
            <div>🏷️</div>
            <p>Sélectionnez des produits pour générer les étiquettes</p>
          </div>
        ) : (
          <div className="etiquettes-grille">
            {produitsSelectionnes.flatMap(p =>
              Array(config.copies).fill(null).map((_, i) => (
                <div key={`${p.id}-${i}`}
                  className="etiquette"
                  style={{
                    width: tailleConfig[config.taille].width,
                    minHeight: tailleConfig[config.taille].height,
                    fontSize: tailleConfig[config.taille].fontSize,
                  }}>
                  <div className="etq-header">
                    <span className="etq-entreprise">{config.nomEntreprise}</span>
                    {config.afficherCategorie && p.categorie && (
                      <span className="etq-categorie">{p.categorie}</span>
                    )}
                  </div>
                  <div className="etq-nom">{p.nom}</div>
                  {config.afficherMarque && p.marque && (
                    <div className="etq-marque">{p.marque}</div>
                  )}
                  {config.afficherReference && p.reference && (
                    <div className="etq-ref">Réf: {p.reference}</div>
                  )}
                  <div className="etq-prix">
                    {fmt(getPrix(p))} FCFA
                    {config.typePrix !== 'ordinaire' && (
                      <span className="etq-prix-type">
                        ({config.typePrix === 'revendeur' ? 'Revendeur' : 'Technicien'})
                      </span>
                    )}
                  </div>
                  {config.afficherCodeBarres && p.codeBarres && (
                    <div className="etq-code">
                      <div className="etq-barres">
                        {p.codeBarres.split('').map((c, idx) => (
                          <div key={idx} className="barre" style={{
                            width: parseInt(c) % 2 === 0 ? '1px' : '2px',
                            background: '#000',
                            height: '100%',
                            display: 'inline-block',
                          }} />
                        ))}
                      </div>
                      <div className="etq-code-txt">{p.codeBarres}</div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}